import { getExternalUploadRetryDelay } from '@sosocio/tools/external-upload';

export {
	EXTERNAL_UPLOAD_RETRY_BASE_MS,
	EXTERNAL_UPLOAD_RETRY_MAX_MS,
	getExternalUploadRetryDelay,
} from '@sosocio/tools/external-upload';

export class ExternalUploadRetryState {
	private attempt = 0;

	/** @returns Whether at least one consecutive reconnect has been scheduled. */
	public get hasRetried(): boolean {
		return this.attempt > 0;
	}

	/** Resets backoff only after channel activity proves the subscription is live. */
	public confirmActivity(): void {
		this.attempt = 0;
	}

	/** Resets backoff when a deliberate scope/channel teardown starts fresh. */
	public reset(): void {
		this.attempt = 0;
	}

	/**
	 * Returns the next delay and advances the consecutive failure count.
	 * Observable construction alone never calls this reset path, so repeated
	 * runtime failures keep increasing their delay until confirmed activity.
	 *
	 * @param random - Jitter source passed to the delay calculator.
	 * @returns Delay for the reconnect being scheduled.
	 */
	public nextDelay(random = Math.random()): number {
		const delay = getExternalUploadRetryDelay(
			this.attempt,
			random,
		);
		this.attempt += 1;
		return delay;
	}
}

export type ExternalUploadSubscriptionEndOptions = {
	activeSession: number;
	currentSession: () => number;
	markConnectionLost: () => void;
	scheduleReconnect: () => void;
	unsubscribe: () => void;
};

/**
 * Handles either an errored or cleanly completed subscription without allowing
 * a late callback from a superseded generation to tear down the live channel.
 *
 * @param options - Generation check and channel lifecycle callbacks.
 * @returns Whether the ended subscription still owned the active generation.
 */
export function handleExternalUploadSubscriptionEnd(
	options: ExternalUploadSubscriptionEndOptions,
): boolean {
	if (options.activeSession !== options.currentSession()) {
		return false;
	}

	options.unsubscribe();
	options.markConnectionLost();
	options.scheduleReconnect();
	return true;
}

export type ExternalUploadSubscriptionRenewalOptions = {
	subscribe: () => void;
	unsubscribe: () => void;
};

/**
 * Invalidates a retained subscription generation before creating its replacement.
 *
 * @param options - Subscription teardown and creation callbacks.
 */
export function renewExternalUploadSubscription(
	options: ExternalUploadSubscriptionRenewalOptions,
): void {
	options.unsubscribe();
	options.subscribe();
}
